/**
 * Quién puede entrar a la consola.
 *
 * 🚨 Lo pidió Luis: «la página administrativa para que el administrador sea el
 * que crea los nuevos logísticos».
 *
 * 🔑 Aquí NO se crean contraseñas. Se **autoriza un correo** —con su nombre y
 * su rol— y esa persona entra con la clave de su propio correo de la empresa.
 * Si no está en esta lista, no entra aunque su clave sea correcta.
 *
 * ⛔ Y no se borra a nadie: se suspende. Lo que esa persona aprobó o devolvió
 * sigue con su nombre, que es justamente para lo que sirven las cuentas.
 */

import { useCallback, useEffect, useState } from 'react'
import { pedirConSesion, quienEntro } from './Entrar.tsx'
import { comoFecha } from './formato.ts'

type Rol = 'ADMIN' | 'LOGISTICO' | 'CONSULTA'

interface Persona {
  id: string
  nombre: string | null
  correo: string | null
  rol: string | null
  estado: string
  ultimo_acceso: string | null
}

const ROLES: { valor: Rol; titulo: string; explica: string }[] = [
  {
    valor: 'LOGISTICO',
    titulo: 'Logístico',
    explica: 'Trabaja los viajes: revisa entregas, sube manifiestos y da acceso a conductores.',
  },
  {
    valor: 'ADMIN',
    titulo: 'Administrador',
    explica: 'Todo lo del logístico y además decide quién más entra a la consola.',
  },
  {
    valor: 'CONSULTA',
    titulo: 'Solo consulta',
    explica: 'Puede mirar, no puede cambiar nada.',
  },
]

/*
 * 🚨 Las fechas se parsean con `comoFecha`, NUNCA con `new Date()` a pelo.
 *
 * Postgres devuelve `2026-08-20 19:23:50.737-05` y JavaScript **no sabe leer
 * ese `-05`**: el estándar pide `-05:00`. La fecha queda inválida y la pantalla
 * dice «Nunca» sobre alguien que sí entró — se vio así, con una fila diciendo
 * «Nunca entró» y al lado «Entró, pero no ha entregado».
 *
 * ⛔ El error estaba en CINCO pantallas porque cada vez volví a escribir la
 * función en vez de usar la que ya existía en `formato.ts`.
 */
function comoSeLlamaElRol(rol: string | null): string {
  return ROLES.find((r) => r.valor === rol)?.titulo ?? 'Sin rol'
}

/** Un día legible, o «nunca». */
function cuando(iso: string | null): string {
  if (!iso) return 'Nunca ha entrado'
  const f = comoFecha(iso)
  if (!f) return '—'
  return f.toLocaleDateString('es-CO', { day: 'numeric', month: 'short', year: 'numeric' })
}

export function Personas() {
  const [lista, setLista] = useState<Persona[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [aviso, setAviso] = useState<string | null>(null)
  const [abriendo, setAbriendo] = useState(false)
  const [guardando, setGuardando] = useState(false)
  const [cambiando, setCambiando] = useState<string | null>(null)

  const [nombre, setNombre] = useState('')
  const [correo, setCorreo] = useState('')
  const [rol, setRol] = useState<Rol>('LOGISTICO')

  const yo = quienEntro()
  /*
   * 🚨 Quien entró con la clave compartida NO tiene rol: `quienEntro()` es
   * nulo. Se le deja mirar y autorizar —si no, nadie podría autorizar al primer
   * administrador— pero la pantalla se lo dice, para que sepa que lo que haga
   * no queda a nombre de nadie.
   */
  const puedeCambiar = yo === null || yo.rol === 'ADMIN'

  const cargar = useCallback(async () => {
    try {
      const r = await pedirConSesion<{ personas: Persona[] }>('/api/admin/interno/personas')
      setLista(r.personas)
      setError(null)
    } catch (e) {
      setError(e instanceof Error ? e.message : 'No se pudo cargar')
    }
  }, [])

  useEffect(() => {
    void cargar()
  }, [cargar])

  async function autorizar(e: React.FormEvent) {
    e.preventDefault()
    if (guardando || !nombre.trim() || !correo.trim()) return
    setGuardando(true)
    setError(null)
    setAviso(null)
    try {
      const r = await pedirConSesion<{ mensaje: string }>('/api/admin/interno/personas', {
        method: 'POST',
        body: JSON.stringify({ nombre: nombre.trim(), correo: correo.trim(), rol }),
      })
      setAviso(r.mensaje)
      setNombre('')
      setCorreo('')
      setRol('LOGISTICO')
      setAbriendo(false)
      await cargar()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo autorizar')
    } finally {
      setGuardando(false)
    }
  }

  async function cambiarAcceso(p: Persona) {
    const devolver = p.estado === 'SUSPENDIDO'
    if (
      !devolver &&
      !window.confirm(
        [
          `¿Quitarle el acceso a ${p.nombre ?? p.correo}?`,
          '',
          'No se borra nada: lo que ya aprobó o devolvió queda con su nombre.',
          'Puedes devolvérselo cuando quieras.',
        ].join('\n'),
      )
    ) {
      return
    }
    setCambiando(p.id)
    setError(null)
    try {
      const r = await pedirConSesion<{ mensaje: string }>(
        `/api/admin/interno/personas/${p.id}/acceso`,
        { method: 'POST', body: JSON.stringify({ permitir: devolver }) },
      )
      setAviso(r.mensaje)
      await cargar()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'No se pudo cambiar')
    } finally {
      setCambiando(null)
    }
  }

  return (
    <section>
      <div className="tit-fila">
        <h1>Quién entra a la consola</h1>
        <span>
          Autorizas su correo y entra con la clave de ese mismo correo. No hay contraseñas nuevas
          que crear ni reponer, y quien sale de la empresa pierde el acceso solo.
        </span>
      </div>

      <div className="filtros-barra">
        <div className="filtros-fila">
          {puedeCambiar && !abriendo && (
            <button onClick={() => setAbriendo(true)} className="btn-buscar">
              Autorizar a alguien
            </button>
          )}
          <span style={{ fontSize: 13, color: '#6B7789' }}>
            {lista ? `${lista.length} ${lista.length === 1 ? 'persona autorizada' : 'personas autorizadas'}` : ''}
          </span>
        </div>
        {!puedeCambiar && (
        <p className="personas-nota">
          Estás mirando. Para autorizar a alguien tienes que entrar con un correo de administrador.
        </p>
      )}

        {yo === null && (
        <p className="personas-nota">
          Entraste con la clave compartida de la oficina, así que lo que hagas no queda a nombre de
          nadie. Autorízate un correo y entra con él.
        </p>
      )}

        {/* ⛔ Los avisos van DENTRO de la caja, nunca entre los filtros y la lista. */}
        {(error || aviso) && (
          <p className={`linea-msg ${error ? 'linea-msg-mal' : 'linea-msg-bien'}`}>
            {error ?? aviso}
          </p>
        )}
      </div>

      {abriendo && (
        <form onSubmit={autorizar} className="personas-form">
          <h3 className="text-[16px] font-semibold">Autorizar a alguien de la oficina</h3>

          <div className="personas-campos">
            <div>
              <label htmlFor="p-nombre" className="personas-etiqueta">
                Nombre completo
              </label>
              <input
                id="p-nombre"
                value={nombre}
                onChange={(e) => setNombre(e.target.value)}
                placeholder="María Fernanda Gómez"
                autoFocus
                className="personas-campo"
              />
              {/* El nombre no es adorno: es lo que va a aparecer al lado de
                  cada entrega que esa persona apruebe o devuelva. */}
              <p className="personas-pista">Es el que va a quedar en lo que apruebe o devuelva.</p>
            </div>

            <div>
              <label htmlFor="p-correo" className="personas-etiqueta">
                Correo de la empresa
              </label>
              <input
                id="p-correo"
                type="email"
                inputMode="email"
                value={correo}
                onChange={(e) => setCorreo(e.target.value)}
                placeholder="nombre@gruposym.co"
                className="personas-campo"
              />
              <p className="personas-pista">Con este correo y su misma clave va a entrar.</p>
            </div>
          </div>

          <p className="personas-etiqueta mt-4">Qué va a poder hacer</p>
          <div className="personas-roles">
            {ROLES.map((r) => (
              <button
                key={r.valor}
                type="button"
                onClick={() => setRol(r.valor)}
                className={`personas-rol ${rol === r.valor ? 'personas-rol-elegido' : ''}`}
              >
                <span className="personas-rol-titulo">{r.titulo}</span>
                <span className="personas-rol-explica">{r.explica}</span>
              </button>
            ))}
          </div>

          <div className="mt-5 flex gap-2">
            <button
              type="submit"
              disabled={guardando || !nombre.trim() || !correo.trim()}
              className="personas-primario"
            >
              {guardando ? 'Autorizando…' : 'Autorizar'}
            </button>
            <button type="button" onClick={() => setAbriendo(false)} className="personas-suave">
              Cancelar
            </button>
          </div>
        </form>
      )}

      {lista === null && !error && <p className="mt-5 text-[15px] text-tinta-suave">Cargando…</p>}

      {lista && lista.length === 0 && (
        <p className="personas-vacio">
          Todavía nadie tiene acceso con su correo. Toda la oficina está entrando con la clave
          compartida, y así no queda registro de quién hace qué.
        </p>
      )}

      {lista && lista.length > 0 && (
        <div className="personas-tarjetas">
          {lista.map((p) => {
            const suspendido = p.estado === 'SUSPENDIDO'
            const nuncaEntro = !p.ultimo_acceso
            return (
              <article key={p.id} className={`persona ${suspendido ? 'persona-fuera' : ''}`}>
                <div className="persona-quien">
                  <span className="persona-inicial">
                    {(p.nombre ?? p.correo ?? '?').trim().charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0">
                    <p className="persona-nombre">{p.nombre ?? 'Sin nombre'}</p>
                    <p className="persona-correo">{p.correo}</p>
                  </div>
                </div>

                <div className="persona-datos">
                  <span className={`persona-rol rol-${p.rol ?? 'nada'}`}>
                    {comoSeLlamaElRol(p.rol)}
                  </span>
                  <span className="persona-cuando">
                    {suspendido ? 'Sin acceso' : nuncaEntro ? 'Nunca ha entrado' : cuando(p.ultimo_acceso)}
                  </span>
                </div>

                {puedeCambiar && (
                  <button
                    onClick={() => void cambiarAcceso(p)}
                    disabled={cambiando === p.id}
                    className={suspendido ? 'persona-devolver' : 'persona-quitar'}
                  >
                    {cambiando === p.id
                      ? 'Un momento…'
                      : suspendido
                        ? 'Devolver el acceso'
                        : 'Quitar el acceso'}
                  </button>
                )}
              </article>
            )
          })}
        </div>
      )}
    </section>
  )
}
